import { useEffect, useState } from "react";
import { useApi, ApiError } from "./api";
import { useChannel, withChannel } from "./channel";

// Framework-free manifest types + pure helpers live in ./course-model (also
// consumed by functions/_lib and the mobile API Worker, which cannot vendor
// React). Re-exported here so existing imports from "./course" keep working.
export * from "./course-model";
import { lessonMdPath, type CourseManifest, type CourseSummary } from "./course-model";

export type ManifestState =
  | { status: "loading" }
  | { status: "ready"; manifest: CourseManifest }
  | { status: "error"; message: string };

// useCourseManifest(slug) — fetches the per-course manifest from the API.
// Pass an empty string to skip the fetch (returns "loading" immediately);
// this lets call sites pass a slug that depends on another async value.
export function useCourseManifest(slug: string): ManifestState {
  const { apiFetch } = useApi();
  const channel = useChannel();
  const [state, setState] = useState<ManifestState>({ status: "loading" });
  useEffect(() => {
    if (!slug) return; // skip fetch until slug is known
    setState({ status: "loading" });
    let cancelled = false;
    apiFetch<CourseManifest>(withChannel(`/api/courses/${slug}/manifest`, channel))
      .then((m) => {
        if (!cancelled) setState({ status: "ready", manifest: m });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
    // apiFetch is intentionally excluded (stable); slug + channel are the real deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, channel]);
  return state;
}

export type CourseIndexState =
  | { status: "loading" }
  | { status: "ready"; courses: CourseSummary[] }
  | { status: "error"; message: string };

export function useCourseIndex(): CourseIndexState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<CourseIndexState>({ status: "loading" });
  useEffect(() => {
    setState({ status: "loading" });
    let cancelled = false;
    apiFetch<CourseSummary[]>("/api/courses/index")
      .then((courses) => {
        if (!cancelled) setState({ status: "ready", courses });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

export type LessonContentState =
  | { status: "loading" }
  | { status: "ready"; markdown: string }
  | { status: "error"; message: string }
  | { status: "not-found" };

// Lessons are served as raw .md from /public/course/. Auth is not required for
// reading the static file in dev/prod — the manifest endpoint (which requires
// auth) is the gate. If we want stricter gating later we can move this through
// an authenticated Function (it would just stream the same bytes).
export function useLessonContent(
  courseSlug: string,
  chapterFolder: string,
  lessonSlug: string
): LessonContentState {
  const channel = useChannel();
  const [state, setState] = useState<LessonContentState>({ status: "loading" });
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetch(withChannel(lessonMdPath(courseSlug, chapterFolder, lessonSlug), channel))
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: "not-found" });
          return;
        }
        if (!res.ok) {
          setState({ status: "error", message: `HTTP ${res.status}` });
          return;
        }
        const text = await res.text();
        setState({ status: "ready", markdown: text });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Unknown error"
        });
      });
    return () => {
      cancelled = true;
    };
  }, [courseSlug, chapterFolder, lessonSlug, channel]);
  return state;
}

// Strips the frontmatter block from a raw lesson MD so the renderer only
// sees the body. The build-content step already exposes the frontmatter via
// the manifest, so we don't need it on the rendering side.
export function stripFrontmatter(markdown: string): string {
  return markdown.replace(/^---\n[\s\S]*?\n---\n/, "");
}

export type TextAssetState =
  | { status: "loading" }
  | { status: "ready"; text: string }
  | { status: "not-found" }
  | { status: "error"; message: string };

// Fetches a raw text asset (Python file, etc.) from the static /course/ tree.
// Returns "not-found" on 404 — callers can render a friendly empty editor or
// skip the IDE entirely. Other non-2xx responses surface as "error".
export function useTextAsset(url: string): TextAssetState {
  const [state, setState] = useState<TextAssetState>({ status: "loading" });
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetch(url)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: "not-found" });
          return;
        }
        if (!res.ok) {
          setState({ status: "error", message: `HTTP ${res.status}` });
          return;
        }
        const text = await res.text();
        setState({ status: "ready", text });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Unknown error"
        });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return state;
}

export type DataFilesState =
  | { status: "loading" }
  | { status: "ready"; files: Record<string, string> }
  | { status: "error"; message: string };

// Fetches each path in `relPaths` from /course/<slug>/<chapterFolder>/<relPath>
// and returns a Record keyed by the path relative to /working/ (the Pyodide FS
// mount root). For Pyodide, "data/deck.txt" maps to "deck.txt" — we strip
// the leading "data/" because /working/ IS the project root.
export function useProjectDataFiles(
  courseSlug: string,
  chapterFolder: string,
  relPaths: string[]
): DataFilesState {
  const channel = useChannel();
  const [state, setState] = useState<DataFilesState>({ status: "loading" });
  // Memoize the joined paths so changing the array reference but not the contents
  // doesn't re-trigger fetches.
  const key = relPaths.join("|");
  useEffect(() => {
    let cancelled = false;
    if (relPaths.length === 0) {
      setState({ status: "ready", files: {} });
      return;
    }
    setState({ status: "loading" });
    Promise.all(
      relPaths.map(async (rel) => {
        const res = await fetch(
          withChannel(`/course/${courseSlug}/${chapterFolder}/${rel}`, channel)
        );
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${rel}`);
        const text = await res.text();
        // Strip "data/" prefix so paths are relative to /working/ in Pyodide.
        const mountPath = rel.startsWith("data/") ? rel.slice("data/".length) : rel;
        return [mountPath, text] as const;
      })
    )
      .then((pairs) => {
        if (cancelled) return;
        const files: Record<string, string> = {};
        for (const [p, t] of pairs) files[p] = t;
        setState({ status: "ready", files });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Unknown error"
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, chapterFolder, key, channel]);
  return state;
}

export function finalExamMdPath(courseSlug: string): string {
  return `/course/${courseSlug}/final-exam.md`;
}
